import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CryptoCardHero } from '../components/CryptoCardHero.js';
import { AppIcon } from '../components/AppIcon.js';
import { SubscriptionSection } from '../components/SubscriptionSection.js';
import { LandingFAQ } from '../components/LandingFAQ.js';
import { PaymentBrandRow } from '../components/PaymentBrandRow.js';
import '../styles/landing-spending.css';
import { WalletBadges, CryptoTokens } from '../components/LandingVisuals.js';

export const LandingPage: React.FC = () => {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.target instanceof HTMLElement) {
            entry.target.dataset.entered = 'true';
          }
          if (entry.target instanceof HTMLElement)
            entry.target.dataset.visible = String(entry.isIntersecting);
        }
      },
      { threshold: 0.25 }
    );
    root.current
      ?.querySelectorAll(
        '.crypto-card-hero, .payment-link-visual, .services-visual, .subscription-visual'
      )
      .forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);
  return (
    <div className="landing" ref={root}>
      <section className="landing-hero">
        <div className="hero-copy">
          <span className="eyebrow">
            <span className="status-dot" /> CRYPTO TO CARD
          </span>
          <h1>
            Spend your <span className="hero-gold-text">crypto</span> <br />
            with a virtual card.
          </h1>
          <p>
            A simpler way to use a virtual card for online purchases and subscriptions. KemyCard
            services are being connected. Funding is not enabled yet.
          </p>
          <PaymentBrandRow />
          <div className="hero-actions">
            <Link to="/preview" className="ios-btn-primary">
              View app preview <AppIcon name="arrow" />
            </Link>
            <a href="#how-it-works" className="quiet-link">
              How it works ↓
            </a>
          </div>
          <span className="hero-caption">Interactive preview · Financial services coming soon</span>
          <p className="signup-identity-note">
            No ID upload to create an account. Card requirements vary by provider.
          </p>
        </div>
        <CryptoCardHero />
      </section>
      <section id="how-it-works" className="feature-strip" aria-label="How PAY888 works">
        <div className="feature-item">
          <span className="feature-step">
            <AppIcon name="wallet" /> 01 / TOP UP
          </span>
          <h2>Add crypto</h2>
          <p>Funding options will be shown after the provider connection is verified.</p>
          <CryptoTokens />
        </div>
        <div className="feature-item">
          <span className="feature-step">
            <AppIcon name="card" /> 02 / FUND
          </span>
          <h2>Fund your card</h2>
          <p>Choose a virtual card and move funds onto it.</p>
          <span className="step-detail">Choose → Review → Fund</span>
        </div>
        <div className="feature-item">
          <span className="feature-step">
            <AppIcon name="card" /> 03 / SPEND
          </span>
          <h2>Pay with your card</h2>
          <p>Shop online or add an eligible card to your wallet.</p>
          <WalletBadges />
        </div>
      </section>
      <SubscriptionSection />
      <section id="services" className="landing-services" aria-labelledby="services-heading">
        <div className="section-intro">
          <span className="eyebrow">KEMYCARD · CURRENT ROLLOUT</span>
          <h2 id="services-heading">
            More for your
            <br />
            digital life.
          </h2>
          <p>
            We are focusing on virtual cards. Bank payouts need a separate verified integration.
            Additional services are not enabled on the current plan.
          </p>
          <Link to="/preview/services" className="quiet-link">
            Explore services <AppIcon name="arrow" />
          </Link>
          <p className="section-note">No deposits, card orders or payouts are active yet.</p>
        </div>
      </section>
      <LandingFAQ />
      <section className="landing-close" aria-labelledby="preview-heading">
        <div>
          <span className="eyebrow">TAKE A LOOK INSIDE</span>
          <h2 id="preview-heading">Explore before you start.</h2>
          <p>No sign-up needed to try the preview.</p>
        </div>
        <Link to="/preview" className="ios-btn-primary">
          Open the preview <AppIcon name="arrow" />
        </Link>
      </section>
    </div>
  );
};
